import {Observable, of, pipe, Subject} from 'rxjs';
import {filter, mergeMap} from 'rxjs/operators';
import {SmartSecurityService} from '../../../components/smart-security';
import {WebSocketMessage} from '../../../components/streaming/streaming-interfaces';
import {
    ArrayEncodedMdMessage,
    ArrayEncodedMdSnapshotMessage
} from '../../../models/common-ui-market-data/array-encoded-md-message';
import {DEFAULT_AGGREGATION_SCALE} from './aggregation';
import {SecurityMarketData} from './md-subscription-subject';
import {SmartMdSubscriptionSubject} from './smart-md-subscription-subject';
import {VwapMdSubscriptionSubject} from './vwap-md-subscription-subject';
import {normalizeVwapSizes, vwapSizeToStr} from './vwap-utils';

enum SmartVwapMdWebSocketMessageType {
    VWAP_MD_SNAPSHOT = 'SMART_VWAP_MD_SNAPSHOT'
}

interface SmartVwapMdWebSocketMessage extends WebSocketMessage {
    messageType: SmartVwapMdWebSocketMessageType.VWAP_MD_SNAPSHOT;
    data: ArrayEncodedMdMessage[];
}

export class SmartVwapMdSubscriptionSubject extends SmartMdSubscriptionSubject {

    public static getSubscriptionDictKey(vwapSizes: number[] | undefined): string {
        return VwapMdSubscriptionSubject.getSubscriptionDictKey(vwapSizes);
    }

    private static readonly sharedActualizeUnsubscription$ = new Subject<void>();

    public messageType: string;

    protected readonly isReadyToCreateSubjectType: boolean;

    constructor(
        protected vwapSizes: number[] | undefined,
        protected override smartSecurityService: SmartSecurityService
    ) {
        super(DEFAULT_AGGREGATION_SCALE, smartSecurityService);

        // Skip early calling createSubjectType from super class
        this.isReadyToCreateSubjectType = true;

        this.createSubjectType();
    }

    protected override createSubjectType(): void {

        // Skip early calling from super class
        if (this.isReadyToCreateSubjectType) {
            const normalizedVwapSizes = normalizeVwapSizes(this.vwapSizes);
            const vwapSuffix = normalizedVwapSizes ? `#${normalizedVwapSizes.map(vwapSizeToStr).join(',')}` : '#auto';
            const messageType = `${SmartVwapMdWebSocketMessageType.VWAP_MD_SNAPSHOT}${vwapSuffix}`;

            this.messageType = messageType;

            this.type = {
                name: `SMART_VWAP_MD${vwapSuffix}`,
                isMultiSubject: true,
                operations:                     {
                    SUBSCRIBE: `SUBSCRIBE_SMART_VWAP_MD${vwapSuffix}`,
                    UNSUBSCRIBE: `UNSUBSCRIBE_SMART_VWAP_MD${vwapSuffix}`
                },
                messagePipe:                    pipe(
                    filter((webSocketMessage: WebSocketMessage) => webSocketMessage.messageType === messageType),
                    mergeMap((mdWebSocketMessage: SmartVwapMdWebSocketMessage): Observable<SecurityMarketData> => {
                        const mdMessageContainers = this.readMdSnapshot(
                            mdWebSocketMessage.data as ArrayEncodedMdSnapshotMessage[]
                        );

                        return of(...mdMessageContainers);
                    })
                ),
                sharedActualizeUnsubscription$: SmartVwapMdSubscriptionSubject.sharedActualizeUnsubscription$,
                subjectPipeFactory: subject => this.createSmartSubjectPipe(subject),
                deprecateSubject: subject => this.deprecateSmartSubject(subject)
            };
        }
    }

}
